import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  DataUsage,
  Science,
  ModelTraining,
  CheckCircle,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useData } from '../contexts/DataContext';
import { useModel } from '../contexts/ModelContext';
import MainLayout from '../components/Layout/MainLayout';

const ProjectDashboard = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { currentProject, fetchProject, fetchSensors, fetchLabels, sensors, labels } = useProject();
  const { fetchSampleCounts, sampleCounts } = useData();

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchProject(projectId);
        await fetchSensors(projectId);
        await fetchLabels(projectId);
        await fetchSampleCounts(projectId);
      } catch (error) {
        console.error('Failed to load project data:', error);
      }
    };

    if (projectId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const progressCards = [
    {
      title: 'Data Import',
      icon: <DataUsage sx={{ fontSize: 40 }} />,
      completed: sensors.length > 0,
      description: `${sensors.length} sensor${sensors.length !== 1 ? 's' : ''} configured`,
      color: theme.palette.primary.main,
    },
    {
      title: 'Data Labeling',
      icon: <Science sx={{ fontSize: 40 }} />,
      completed: sampleCounts.labeled > 0,
      description: `${sampleCounts.labeled || 0} sample${sampleCounts.labeled !== 1 ? 's' : ''} labeled`,
      color: theme.palette.info.main,
    },
    {
      title: 'Model Training',
      icon: <ModelTraining sx={{ fontSize: 40 }} />,
      completed: labels.length > 0,
      description: `${labels.length} label${labels.length !== 1 ? 's' : ''} configured`,
      color: theme.palette.success.main,
    },
  ];

  const statCards = [
    {
      label: 'Total Samples',
      value: sampleCounts.total_samples || 0,
      color: theme.palette.primary.main,
    },
    {
      label: 'Labeled',
      value: sampleCounts.labeled || 0,
      color: theme.palette.success.main,
    },
    {
      label: 'Parked',
      value: sampleCounts.parked || 0,
      color: theme.palette.warning.main,
    },
    {
      label: 'Cortex',
      value: sampleCounts.cortex || 0,
      color: theme.palette.info.main,
    },
  ];

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            {currentProject?.name || 'Project Dashboard'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentProject?.description || 'Project overview and progress tracking'}
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${alpha(
                    stat.color,
                    0.7
                  )} 100%)`,
                  color: 'white',
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h3" fontWeight={700} gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Workflow Progress */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Workflow Progress
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Track your progress through the analysis workflow
            </Typography>

            <Grid container spacing={3}>
              {progressCards.map((card, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: alpha(card.color, 0.05),
                      border: `2px solid ${alpha(card.color, 0.2)}`,
                      position: 'relative',
                    }}
                  >
                    {card.completed && (
                      <Chip
                        icon={<CheckCircle />}
                        label="Completed"
                        size="small"
                        color="success"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        background: alpha(card.color, 0.1),
                        color: card.color,
                        mb: 2,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Sensors and Labels Overview */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Sensors
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {Array.isArray(sensors) ? sensors.length : 0} configured sensor{(Array.isArray(sensors) && sensors.length !== 1) ? 's' : ''}
                </Typography>
                {!Array.isArray(sensors) || sensors.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No sensors configured yet
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sensors.slice(0, 5).map((sensor) => (
                      <Chip
                        key={sensor.id}
                        label={sensor.name}
                        variant="outlined"
                        sx={{ justifyContent: 'flex-start' }}
                      />
                    ))}
                    {sensors.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        +{sensors.length - 5} more
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Labels / Predictors
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {Array.isArray(labels) ? labels.length : 0} configured label{(Array.isArray(labels) && labels.length !== 1) ? 's' : ''}
                </Typography>
                {!Array.isArray(labels) || labels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No labels configured yet
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {labels.slice(0, 5).map((label) => (
                      <Chip
                        key={label.id}
                        label={label.name}
                        color="primary"
                        variant="outlined"
                        sx={{ justifyContent: 'flex-start' }}
                      />
                    ))}
                    {labels.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        +{labels.length - 5} more
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </MainLayout>
  );
};

export default ProjectDashboard;
